import React from "react";
import {
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { SxProps, Theme } from "@mui/material/styles";

export interface RosterEditorRow {
  rowId: string;
  name: string;
  email: string;
  electee: boolean;
  elector: boolean;
}

interface RosterEditorTableProps {
  rows: RosterEditorRow[];
  editable: boolean;
  nameLabel: string;
  emailLabel: string;
  electeeLabel: string;
  electorLabel: string;
  actionLabel: string;
  removeLabel: string;
  showEmailAndRoles?: boolean;
  compactHeaders?: boolean;
  stickyHeader?: boolean;
  containerSx?: SxProps<Theme>;
  onRowChange: (rowId: string, updates: Partial<RosterEditorRow>) => void;
  onRowRemove: (rowId: string) => void;
  rowHasError?: (row: RosterEditorRow) => boolean;
  emailHasError?: (row: RosterEditorRow) => boolean;
}

export default function RosterEditorTable({
  rows,
  editable,
  nameLabel,
  emailLabel,
  electeeLabel,
  electorLabel,
  actionLabel,
  removeLabel,
  showEmailAndRoles = true,
  compactHeaders = false,
  stickyHeader = false,
  containerSx,
  onRowChange,
  onRowRemove,
  rowHasError,
  emailHasError,
}: RosterEditorTableProps): JSX.Element {
  return (
    <TableContainer
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflowX: "auto",
        ...containerSx,
      }}
    >
      <Table stickyHeader={stickyHeader} size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: showEmailAndRoles ? "34%" : "82%", fontSize: "0.82rem", px: 0.75, whiteSpace: "nowrap" }}>
              {nameLabel}
            </TableCell>
            {showEmailAndRoles && (
              <>
                <TableCell sx={{ width: "40%", fontSize: "0.82rem", px: 0.75, whiteSpace: "nowrap" }}>
                  {emailLabel}
                </TableCell>
                <TableCell align="center" sx={{ width: "8%", px: 0.25, whiteSpace: "nowrap" }}>
                  {compactHeaders ? <Tooltip title={electeeLabel}><span>E</span></Tooltip> : electeeLabel}
                </TableCell>
                <TableCell align="center" sx={{ width: "8%", px: 0.25, whiteSpace: "nowrap" }}>
                  {compactHeaders ? <Tooltip title={electorLabel}><span>R</span></Tooltip> : electorLabel}
                </TableCell>
              </>
            )}
            <TableCell align="center" sx={{ width: showEmailAndRoles ? "10%" : "18%", px: 0.25, whiteSpace: "nowrap" }}>
              {compactHeaders ? <Tooltip title={actionLabel}><span>A</span></Tooltip> : actionLabel}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.rowId}
              hover
              sx={rowHasError && rowHasError(row) ? { outline: "1.5px solid", outlineColor: "error.main", outlineOffset: "-1px" } : {}}
            >
              <TableCell sx={{ py: 0.25, px: 0.75 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={row.name}
                  onChange={(e) => onRowChange(row.rowId, { name: e.target.value })}
                  disabled={!editable}
                  inputProps={{ style: { fontSize: "0.84rem", paddingTop: 4, paddingBottom: 4 } }}
                  sx={{ "& .MuiOutlinedInput-root": { height: 30 } }}
                />
              </TableCell>

              {showEmailAndRoles && (
                <>
                  <TableCell sx={{ py: 0.25, px: 0.75 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.email}
                      onChange={(e) => onRowChange(row.rowId, { email: e.target.value })}
                      disabled={!editable}
                      error={Boolean(emailHasError && emailHasError(row))}
                      inputProps={{ style: { fontSize: "0.84rem", paddingTop: 4, paddingBottom: 4 } }}
                      sx={{ "& .MuiOutlinedInput-root": { height: 30 } }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                    <Checkbox
                      checked={row.electee}
                      onChange={(e) => onRowChange(row.rowId, { electee: e.target.checked })}
                      disabled={!editable}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                    <Checkbox
                      checked={row.elector}
                      onChange={(e) => onRowChange(row.rowId, { elector: e.target.checked })}
                      disabled={!editable}
                      size="small"
                    />
                  </TableCell>
                </>
              )}

              <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                <Tooltip title={removeLabel}>
                  <span>
                    <IconButton
                      color="error"
                      size="small"
                      disabled={!editable}
                      onClick={() => onRowRemove(row.rowId)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
